import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { format, parseISO } from "date-fns";
import { DollarSign } from "lucide-react";
import { InvoiceGroupedList } from "@/components/invoices/InvoiceGroupedList";

interface Invoice {
  id: string;
  payment_amount: number;
  supplier_name: string;
  supplier_email: string;
  discount_amount: number | null;
  discounted_payment_amount: number | null;
  status: string;
  created_at: string;
  payment_due_date: string;
}

interface SavingsVisualizationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoices: Invoice[];
}

export function SavingsVisualization({ open, onOpenChange, invoices }: SavingsVisualizationProps) {
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  const accelerated = invoices.filter(
    (i) =>
      i.status === "early_payment_scheduled" ||
      i.status === "early_payment_completed" ||
      i.status === "early_payment" ||
      i.status === "paid"
  );

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const monthlyData: Record<string, { month: string; savings: number; invoices: Invoice[] }> = {};
  accelerated.forEach((inv) => {
    const monthKey = format(parseISO(inv.created_at), "yyyy-MM");
    if (!monthlyData[monthKey])
      monthlyData[monthKey] = { month: monthKey, savings: 0, invoices: [] };
    monthlyData[monthKey].savings += inv.discount_amount || 0;
    monthlyData[monthKey].invoices.push(inv);
  });

  const chartData = Object.values(monthlyData)
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((d) => ({
      ...d,
      label: format(
        new Date(parseInt(d.month.split("-")[0]), parseInt(d.month.split("-")[1]) - 1, 1),
        "MMM yyyy"
      ),
    }));

  const totalSaved = accelerated.reduce((s, i) => s + (i.discount_amount || 0), 0);
  const selectedMonthData = selectedMonth ? monthlyData[selectedMonth] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-success" />
            Savings with DiscoRun AI
          </DialogTitle>
          <DialogDescription>
            Total saved: {formatCurrency(totalSaved)} across {accelerated.length} invoices
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`}
                  tick={{ fontSize: 12 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <Tooltip
                  formatter={(value: number) => [formatCurrency(value), "Savings"]}
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                  }}
                />
                <Bar
                  dataKey="savings"
                  radius={[4, 4, 0, 0]}
                  cursor="pointer"
                  onClick={(data) => setSelectedMonth(data.month)}
                >
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.month}
                      fill={
                        entry.month === selectedMonth
                          ? "hsl(var(--primary))"
                          : "hsl(var(--success))"
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-muted-foreground py-8">No savings data yet</p>
          )}
        </div>

        {selectedMonthData && (
          <div className="border-t border-border pt-4">
            <h4 className="text-sm font-semibold mb-3">
              {format(
                new Date(
                  parseInt(selectedMonthData.month.split("-")[0]),
                  parseInt(selectedMonthData.month.split("-")[1]) - 1,
                  1
                ),
                "MMMM yyyy"
              )}{" "}
              — {formatCurrency(selectedMonthData.savings)} saved
            </h4>
            <InvoiceGroupedList
              invoices={selectedMonthData.invoices}
              emptyMessage="No invoices this month"
              maxHeight="200px"
              showStatus={false}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
